import { useEffect, useRef, useState } from "react";
import { CursorType } from "../cursor-type";
import styles from "./side-menu.module.css";
import { useBoardValue } from "frontend/state/board-state";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { MenuItemCIM } from "./side-menu-items/menu-item-cim";
import SubMenu from "../sub-menu";
import { createIntegrationTypeAtom, documentIdAtom, submenuDataAtom } from "state-atoms";
import useStateValue from "frontend/state/value";
import { InstanceType } from "shared/datamodel/schemas/ability";
import useAbility from "frontend/hooks/use-ability";
import { signUpToContinueModalAtom } from "state-atoms/modal-atoms";
import * as items from "./side-menu-items/side-menu-items";
import { useBoardIntegrations } from "frontend/hooks/use-board-integrations";
import SyncMondayBoardModal from "frontend/canvas-designer-new/integrations/modals/sync-monday-board/sync-monday-board-modal";
import { useModal } from "frontend/modal/dynamic-modal";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { CanvasSideMenuProps } from "./side-menu";

const Separator = () => <div style={{ width: "1px", height: "100%", borderRight: "1px solid #EBEDF3" }} />;

export function CanvasSideMenuCIM({
  currentCursorType,
  onChangeCursorType,
  setShowSidePanel,
  toggleShowIntegrations,
  showSubmenu,
  setShowSubmenu,
  setCursorType,
  setShowRestrictedModal,
}: Omit<CanvasSideMenuProps, "isCimReorderEnabled">) {
  // Atoms
  const [submenuData, setSubmenuData] = useAtom(submenuDataAtom);
  const setShowSignUpToContinueModal = useSetAtom(signUpToContinueModalAtom);
  const createIntegrationType = useAtomValue(createIntegrationTypeAtom);
  const hasCalledIntegrationClick = useRef(false);

  // Flags
  const isCimReorderImportMondayItemsEnabled = useFeatureFlag("cim-reorder-import-monday-items");

  // State
  const [{ config: boardConfig, user, board }, dispatch] = useBoardValue();
  const [globalState] = useStateValue();
  const { canPerformAnyAction } = useAbility();

  const [refToCursor, setRefToCursor] = useState(null);

  // Menu layout
  const menuRef = useRef<HTMLDivElement>(null);
  const documentId = useAtomValue(documentIdAtom).documentId;
  const { boardIntegrations, mondayIntegration } = useBoardIntegrations(documentId);

  const { showModal } = useModal();

  function onItemClick(item: items.MenuItem, ref: any = null) {
    if (globalState.user && board && !canPerformAnyAction(globalState.user, board, InstanceType.Board)) {
      setShowRestrictedModal(true);
      return;
    }

    if (item.type === CursorType.mondayIntegration) {
      if (user?.isAnonymous) {
        setShowSignUpToContinueModal(true);
        return;
      }

      // new import monday items flow
      if (isCimReorderImportMondayItemsEnabled) {
        setRefToCursor(ref);
        onChangeCursorType(item.type);
        return;
      }

      // old import monday items flow
      if (boardIntegrations?.length === 0) {
        showModal(SyncMondayBoardModal, toggleShowIntegrations, {
          integrationId: mondayIntegration?.id ?? "",
        });
      } else {
        toggleShowIntegrations();
      }

      return;
    }

    const validTypes = [CursorType.move, CursorType.pointer];
    const signupOnlyItems = [CursorType.comment, CursorType.template];
    const showSignupToAnonymousUser = user?.isAnonymous && signupOnlyItems.includes(item.type);
    if (showSignupToAnonymousUser) {
      setShowSignUpToContinueModal(true);
      return;
    }
    if (boardConfig.isViewOnly && !validTypes.includes(item.type)) {
      dispatch({ type: "show_signup", payload: true });
    } else {
      // Add option to deselect the pointer, and switch to move cursor.
      if (item.type === CursorType.pointer && currentCursorType === CursorType.pointer) {
        setRefToCursor(ref);
        onChangeCursorType(CursorType.move);
        return;
      }

      setRefToCursor(ref);
      onChangeCursorType(item.type);
    }
  }

  function isHighlighted(item: items.MenuItem) {
    return item.isHighlighted ? item.isHighlighted(currentCursorType) : currentCursorType === item.type;
  }

  useEffect(() => {
    if (createIntegrationType === "monday-tasks" && !hasCalledIntegrationClick.current) {
      // Wait for the DOM to be ready before querying for the element
      requestAnimationFrame(() => {
        const element = document.getElementById(items.getMenuItemIdSelector(CursorType.mondayIntegration));
        if (element) {
          hasCalledIntegrationClick.current = true;
          const elementRef = { current: element as HTMLDivElement };
          onItemClick(items.cimMenuItems.mondayIntegrationMenuItemCIM, elementRef);
          setCursorType(CursorType.mondayIntegration);
          setShowSubmenu(true);
        }
      });
    }
  }, [createIntegrationType]);

  return (
    <>
      <div data-testid="sidemenu" id="tools-sidemenu" className={styles.cimContainer} ref={menuRef}>
        <div className={styles.cimItemsContainer}>
          <MenuItemCIM
            item={items.cimMenuItems.pointerMenuItem}
            onItemClick={onItemClick}
            highlight={isHighlighted(items.cimMenuItems.pointerMenuItem)}
            setShowSidePanel={setShowSidePanel}
          />

          <MenuItemCIM
            item={items.cimMenuItems.penMenuItem}
            onItemClick={onItemClick}
            highlight={isHighlighted(items.cimMenuItems.penMenuItem)}
            setShowSidePanel={setShowSidePanel}
            style={{ color: submenuData.pen.color }}
          />

          <MenuItemCIM
            item={items.cimMenuItems.stickyNoteMenuItem}
            onItemClick={onItemClick}
            highlight={isHighlighted(items.cimMenuItems.stickyNoteMenuItem)}
            setShowSidePanel={setShowSidePanel}
            style={{ color: submenuData.stickyNote.color }}
          />

          <MenuItemCIM
            item={items.cimMenuItems.shapeMenuItem}
            onItemClick={onItemClick}
            highlight={isHighlighted(items.cimMenuItems.shapeMenuItem)}
            setShowSidePanel={setShowSidePanel}
          />

          <Separator />

          <MenuItemCIM
            item={items.cimMenuItems.textMenuItem}
            onItemClick={onItemClick}
            highlight={isHighlighted(items.cimMenuItems.textMenuItem)}
            setShowSidePanel={setShowSidePanel}
          />
          <MenuItemCIM
            item={items.cimMenuItems.nounIconsMenuItem}
            onItemClick={onItemClick}
            highlight={isHighlighted(items.cimMenuItems.nounIconsMenuItem)}
            setShowSidePanel={setShowSidePanel}
          />
          <MenuItemCIM
            item={items.cimMenuItems.frameMenuItem}
            onItemClick={onItemClick}
            highlight={isHighlighted(items.cimMenuItems.frameMenuItem)}
            setShowSidePanel={setShowSidePanel}
          />

          <MenuItemCIM
            item={items.cimMenuItems.fileMenuItem}
            onItemClick={onItemClick}
            highlight={isHighlighted(items.cimMenuItems.fileMenuItem)}
            setShowSidePanel={setShowSidePanel}
          />
          <MenuItemCIM
            item={items.cimMenuItems.templatesMenuItem}
            onItemClick={onItemClick}
            highlight={isHighlighted(items.cimMenuItems.templatesMenuItem)}
            setShowSidePanel={setShowSidePanel}
          />

          <Separator />

          <MenuItemCIM
            item={items.cimMenuItems.mondayIntegrationMenuItemCIM}
            onItemClick={onItemClick}
            highlight={isHighlighted(items.cimMenuItems.mondayIntegrationMenuItemCIM)}
            setShowSidePanel={setShowSidePanel}
          />
          <MenuItemCIM
            item={items.cimMenuItems.cimWidgetsMenuItem}
            onItemClick={onItemClick}
            highlight={isHighlighted(items.cimMenuItems.cimWidgetsMenuItem)}
            setShowSidePanel={setShowSidePanel}
          />
        </div>
        {showSubmenu && refToCursor && (
          <SubMenu
            showSubmenu={showSubmenu}
            cursorType={currentCursorType}
            setShowSubmenu={setShowSubmenu}
            setSubmenuData={setSubmenuData}
            submenuData={submenuData}
            setCursorType={setCursorType}
            refToCursor={refToCursor}
            isCimReorderEnabled={true}
          />
        )}
      </div>
    </>
  );
}
